import React, { useEffect, useRef, useState } from 'react'
import {
  FastBackwardFilled,
  FastForwardFilled,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  SoundOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import './AudioPlayer.scss'

const staticWaveform = [
  5, 12, 28, 45, 20, 65, 88, 35, 55, 82, 
  40, 70, 50, 85, 35, 60, 90, 45, 75, 30, 
  65, 85, 40, 55, 95, 45, 80, 50, 70, 35, 
  60, 88, 42, 65, 85, 30, 50, 85, 35, 70, 
  45, 90, 40, 75, 35, 65, 95, 50, 80, 45, 
  60, 85, 30, 55, 90, 45, 70, 35, 65, 88, 
  40, 80, 50, 75, 35, 60, 95, 45, 70, 30, 
  55, 85, 40, 65, 90, 35, 50, 30, 15, 5
];

export default function AudioPlayer({
  audioUrl,
  audioName,
  autoPlayNext,
  onPlayPause,
  playPauseIndex = 0
}) {
  const audioRef = useRef(null)
  const progressBarRef = useRef(null)
  const waveBarsRef = useRef([])
  const audioCtxRef = useRef(null)
  const analyserRef = useRef(null)
  const sourceNodeRef = useRef(null)
  const reqFrameRef = useRef(null)
  const pendingSeekRef = useRef(null)
  const prevVolumeRef = useRef(1)

  const [duration, setDuration] = useState(0)
  const [currentProgress, setCurrentProgress] = useState(0)
  const [currentPosition, setCurrentPosition] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(1)
  
  const [crossOriginEnabled, setCrossOriginEnabled] = useState(true)
  const [useFallback, setUseFallback] = useState(false)
  
  const { t } = useTranslation()

  const formatTime = timeInSeconds => {
    if (isNaN(timeInSeconds)) return '0:00'
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
  }

  // Set up Web Audio API seamlessly if browser and CORS allows
  const setupAudioContext = () => {
    if (audioCtxRef.current || useFallback) return

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext
      const audioCtx = new AudioContext()
      const analyser = audioCtx.createAnalyser()
      
      analyser.fftSize = 256 // 128 frequency bins
      
      const source = audioCtx.createMediaElementSource(audioRef.current)
      source.connect(analyser)
      analyser.connect(audioCtx.destination)

      audioCtxRef.current = audioCtx
      analyserRef.current = analyser
      sourceNodeRef.current = source
    } catch (err) {
      console.warn("Web Audio API failed/blocked. Using fast CSS fallback.", err)
      setUseFallback(true)
    }
  }

  useEffect(() => {
    const audio = audioRef.current

    const updateProgress = () => {
      const current = audio.currentTime
      const dur = audio.duration
      setCurrentPosition(current)
      if (dur > 0) {
        const progress = (current / dur) * 100
        setCurrentProgress(progress)
      }
    }

    const updateDuration = () => setDuration(audio.duration)

    const handleEnded = () => {
      setIsPlaying(false)
      if (autoPlayNext) autoPlayNext()
    }

    audio.addEventListener('timeupdate', updateProgress)
    audio.addEventListener('loadedmetadata', updateDuration)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('timeupdate', updateProgress)
      audio.removeEventListener('loadedmetadata', updateDuration)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [autoPlayNext])

  // Real-time animation loop
  useEffect(() => {
    if (!isPlaying || useFallback || !analyserRef.current) {
      if (reqFrameRef.current) cancelAnimationFrame(reqFrameRef.current)
      return
    }

    const analyser = analyserRef.current
    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    const draw = () => {
      reqFrameRef.current = requestAnimationFrame(draw)
      analyser.getByteFrequencyData(dataArray)

      for (let i = 0; i < 80; i++) {
        if (!waveBarsRef.current[i]) continue
        
        // Grab frequency volume from bass to treble and normalize 0-255 into 0-100%
        const value = dataArray[i] || 0
        const heightPercent = Math.max(5, (value / 255) * 100)
        
        // Push styles directly to DOM for smooth 60fps performance without React re-renders
        waveBarsRef.current[i].style.height = `${heightPercent}%`
      }
    }

    draw()

    return () => {
      if (reqFrameRef.current) cancelAnimationFrame(reqFrameRef.current)
    }
  }, [isPlaying, useFallback])


  useEffect(() => {
    const audio = audioRef.current
    if (audioUrl) {
      setCurrentProgress(0)
      setCurrentPosition(0)
      audio.load()
      
      // Auto-play when the audio URL is assigned or changed.
      // The AudioPlayer only mounts when a user clicks a track in the queue,
      // so this safely aligns with explicit user intention.
      setIsPlaying(true)
      audio.play().catch(e => {
        if (e.name !== 'AbortError') {
          setIsPlaying(false)
        }
        console.log("Autoplay prevented:", e)
      })
    }
  }, [audioUrl])

  // Fallback re-loader if CORS was rejected
  useEffect(() => {
    if (!crossOriginEnabled && audioRef.current) {
      const audio = audioRef.current;
      const currentTime = audio.currentTime;
      const wasPlaying = !audio.paused;
      audio.load()
      audio.currentTime = currentTime;
      if (wasPlaying) {
        audio.play().catch(e => {
          if (e.name !== 'AbortError') setIsPlaying(false)
        })
      }
    }
  }, [crossOriginEnabled])

  useEffect(() => {
    if (playPauseIndex !== 0) togglePlay()
  }, [playPauseIndex])

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume
  }, [volume])

  const togglePlay = () => {
    const audio = audioRef.current
    if (audio.paused) {
      setupAudioContext()
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume()
      }
      // Restore pending seek position before playing (Bug 3 fix)
      if (pendingSeekRef.current !== null) {
        audio.currentTime = pendingSeekRef.current
        pendingSeekRef.current = null
      }
      setIsPlaying(true)
      audio.play().catch(err => {
        if (err.name !== 'AbortError') {
          setIsPlaying(false)
        }
        console.log("Play interrupted:", err)
      })
    } else {
      setIsPlaying(false)
      audio.pause()
    }
  }

  const skipPlay = type => {
    const audio = audioRef.current
    const dur = audio.duration || 0
    if (type === 'BACK') {
      audio.currentTime = Math.max(0, audio.currentTime - 10)
    } else {
      audio.currentTime = Math.min(audio.currentTime + 10, dur)
    }
    if (dur > 0) {
      setCurrentProgress((audio.currentTime / dur) * 100)
    }
  }

  const handleProgressBarClick = e => {
    const audio = audioRef.current
    const rect = progressBarRef.current.getBoundingClientRect()
    const clickPos = e.clientX - rect.left
    const ratio = Math.max(0, Math.min(1, clickPos / rect.width))
    if (isFinite(audio.duration) && audio.duration > 0) {
      const seekTime = ratio * audio.duration
      audio.currentTime = seekTime
      setCurrentProgress(ratio * 100)
      setCurrentPosition(seekTime)
      // Persist seek position so it survives pause-seek-play cycle
      if (audio.paused) {
        pendingSeekRef.current = seekTime
      }
    }
  }

  const handleVolumeChange = e => {
    const newVol = Number(e.target.value)
    if (newVol > 0) prevVolumeRef.current = newVol
    setVolume(newVol)
  }

  const toggleMute = () => {
    if (volume === 0) {
      setVolume(prevVolumeRef.current || 1)
    } else {
      prevVolumeRef.current = volume
      setVolume(0)
    }
  }

  const handleAudioError = () => {
    // If the browser blocks decoding because of S3/CDN CORS policies:
    if (crossOriginEnabled) {
      console.warn("CORS block detected. Reverting to safe-mode and CSS fallback.")
      setCrossOriginEnabled(false)
      setUseFallback(true)
    }
  }

  const isEnded = currentProgress >= 99.9;

  const onPlayPauseRef = useRef(onPlayPause)
  useEffect(() => { onPlayPauseRef.current = onPlayPause }, [onPlayPause])
  
  useEffect(() => {
    if (onPlayPauseRef.current) {
      onPlayPauseRef.current(!isPlaying, isEnded)
    }
  }, [isPlaying, isEnded])

  return (
    <div className="ap-spotify-layout">
      <audio 
        ref={audioRef} 
        controls={false}
        crossOrigin={crossOriginEnabled ? "anonymous" : undefined}
        onError={handleAudioError}
      >
        {audioUrl && <source src={audioUrl} type="audio/mpeg" />}
        {t('v4:your_browser_does_not_suppot')}
      </audio>

      {/* Left: Info */}
      <div className="ap-left-col">
        <span className="ap-title" title={audioName || 'Now Playing'}>
          {audioName || 'Now Playing'}
        </span>
      </div>

      {/* Center: Controls & Scrubber */}
      <div className="ap-center-col">
        <div className="ap-controls-center">
          <button
            className="ap-btn ap-btn--skip"
            onClick={() => skipPlay('BACK')}
          >
            <FastBackwardFilled />
          </button>

          <button
            className="ap-btn ap-btn--play"
            onClick={() => togglePlay()}
          >
            {isEnded ? (
              <ReloadOutlined />
            ) : isPlaying ? (
              <PauseCircleOutlined />
            ) : (
              <PlayCircleOutlined />
            )}
          </button>

          <button
            className="ap-btn ap-btn--skip"
            onClick={() => skipPlay('FORWARD')}
          >
            <FastForwardFilled />
          </button>
        </div>

        <div className="ap-progress-row">
          <span className="ap-time ap-time--left">{formatTime(currentPosition)}</span>

          <div
            className="ap-waveform-container"
            ref={progressBarRef}
            onClick={handleProgressBarClick}
          >
            {Array.from({ length: 80 }).map((_, i) => {
              const staticHeight = staticWaveform[i] || 5;
              const percent = (i / 80) * 100;
              const isPlayed = percent <= currentProgress;
              
              const animDuration = `${0.3 + Math.abs(Math.sin((i + 1) * 11) * 0.4)}s`;
              const animDelay = `${-Math.abs(Math.cos((i + 1) * 17) * 2)}s`;
              
              // Only apply animating bounce class if we are forced to use the CSS fallback and not ended
              const cssAnimating = isPlaying && useFallback && !isEnded;
              
              return (
                <div 
                  key={i} 
                  ref={el => waveBarsRef.current[i] = el}
                  className={`ap-wave-bar ${isPlayed ? 'ap-wave-bar--played' : ''} ${cssAnimating ? 'ap-wave-bar--animating' : ''}`}
                  style={{ 
                    // Initial load displays static waveform gracefully. JS will overwrite this height if WebAudio runs.
                    height: `${staticHeight}%`,
                    animationDuration: cssAnimating ? animDuration : '0s',
                    animationDelay: cssAnimating ? animDelay : '0s',
                    transition: (isPlaying && !useFallback && !isEnded) ? 'none' : 'height 0.3s ease-out'
                  }}
                ></div>
              )
            })}
            <div className="ap-wave-center-line">
               <div className="ap-wave-center-line-played" style={{ width: `${currentProgress}%` }}></div>
            </div>
          </div>

          <span className="ap-time ap-time--right">{formatTime(duration)}</span>
        </div>
      </div>

      {/* Right: Volume */}
      <div className="ap-right-col">
        <div className="ap-volume-controls">
          {volume === 0 ? (
            <svg
              className="ap-volume-icon ap-volume-icon--muted"
              onClick={toggleMute}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="18"
              height="18"
            >
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <SoundOutlined className="ap-volume-icon" onClick={toggleMute} />
          )}
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={handleVolumeChange}
            className="ap-volume-slider"
          />
        </div>
      </div>
    </div>
  )
}
